import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  IReduxRequestNode,
  IStandardGUJRequestParams,
  IResponseRedirect,
} from "@litres-libs/types";
import { Request } from "../../request/Request";
import { addHydrateReducer } from "../../utils/hydrateReducer";

interface IUserRegistrationState {
  actions: {
    checkEmailAvailabilityGUJ: TCheckEmailAvailabilityGUJNode;
    verifyPhoneCSRF: TVerifyPhoneCSRFNode;
    recoverPasswordCSRF: TRecoverPasswordCSRFNode;
    registrationCSRF: TRegistrationCSRFNode;
  };
}

type TCheckEmailAvailabilityGUJNode = IReduxRequestNode<
  ICheckEmailAvailabilityRequestGUJ,
  ICheckEmailAvailabilityResponseGUJ
>;
type TVerifyPhoneCSRFNode = IReduxRequestNode<
  IVerifyPhoneRequestCSRF,
  IVerifyPhoneResponseCSRF
>;
type TRecoverPasswordCSRFNode = IReduxRequestNode<
  IRecoverPasswordRequestCSRF,
  IRecoverPasswordResponseCSRF
>;
type TRegistrationCSRFNode = IReduxRequestNode<
  IRegistrationRequestCSRF,
  IRegistrationResponseCSRF
>;

const initialState: IUserRegistrationState = {
  actions: {
    checkEmailAvailabilityGUJ: {},
    verifyPhoneCSRF: {},
    recoverPasswordCSRF: {},
    registrationCSRF: {},
  },
};

interface ICheckEmailAvailabilityParamsGUJ {
  email2check: string;
  tcountry?: string;
}

type ICheckEmailAvailabilityRequestGUJ = ICheckEmailAvailabilityParamsGUJ & {
  gu_ajax: boolean;
};

interface ICheckEmailAvailabilityResponseGUJ {
  status: number;
  err_descr: string;
  has_phone?: number;
  has_email?: number;
  loginformat?: string;
  mail_confirmed?: number;
  phone_verified?: number;
}

export const checkEmailAvailabilityGUJ = createAsyncThunk(
  "user/registration/checkEmailAvailabilityGUJ",
  async (params: {
    data: ICheckEmailAvailabilityParamsGUJ;
    onSuccess?: (success: TCheckEmailAvailabilityGUJNode) => void;
    onError?: (error: TCheckEmailAvailabilityGUJNode) => void;
  }) => {
    const response = new Request.GUJ<
      ICheckEmailAvailabilityRequestGUJ,
      ICheckEmailAvailabilityResponseGUJ
    >({
      url: "/pages/ajax_check_email_availability/",
      provider: {
        id: "checkEmailAvailabilityGUJ",
      },
      request: {
        method: "POST",
        data: {
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface IVerifyPhoneParamsCSRF {
  mail: string;
  tcountry?: string;
  code?: string;
}

type IVerifyPhoneRequestCSRF = IVerifyPhoneParamsCSRF &
  IStandardGUJRequestParams;

interface IVerifyPhoneResponseCSRF {
  status: string;
  actions_output: {
    phone: string;
    success: number;
  };
}

export const verifyPhoneCSRF = createAsyncThunk(
  "user/registration/verifyPhoneCSRF",
  async (params: {
    data: IVerifyPhoneParamsCSRF;
    onSuccess?: (success: TVerifyPhoneCSRFNode) => void;
    onError?: (error: TVerifyPhoneCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IVerifyPhoneRequestCSRF,
      IVerifyPhoneResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "verifyPhoneCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "verify_phone",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface IRecoverPasswordParamsCSRF {
  mail: string;
  tcountry?: string;
  send_to_phone?: number;
  send_to_mail?: number;
  ajax?: number;
}

interface IConfirmRecoverPasswordParamsCSRF {
  key: string;
  user: string;
  no_redirect: number;
}

type IRecoverPasswordRequestCSRF = IRecoverPasswordParamsCSRF &
  IStandardGUJRequestParams;

interface IRecoverPasswordResponseCSRF {
  status: string;
  actions_output: {
    phone?: string;
    mail?: string;
    success: number;
  };
}

export const recoverPasswordCSRF = createAsyncThunk(
  "user/registration/recoverPasswordCSRF",
  async (params: {
    data: IRecoverPasswordParamsCSRF;
    onSuccess?: (success: TRecoverPasswordCSRFNode) => void;
    onError?: (error: TRecoverPasswordCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IRecoverPasswordRequestCSRF,
      IRecoverPasswordResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "recoverPasswordCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "recover_password",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

type IConfirmRecoverPasswordRequestCSRF = IConfirmRecoverPasswordParamsCSRF &
  IStandardGUJRequestParams;
interface IConfirmRecoverPasswordResponseCSRF {
  status: string;
  actions_output: {
    success: number;
  };
}

export const confirmRecoverPasswordCSRF = createAsyncThunk(
  "user/registration/confirmRecoverPasswordCSRF",
  async (params: {
    data: IConfirmRecoverPasswordParamsCSRF;
    onSuccess?: (success: TRecoverPasswordCSRFNode) => void;
    onError?: (error: TRecoverPasswordCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IConfirmRecoverPasswordRequestCSRF,
      IConfirmRecoverPasswordResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "confirmRecoverPasswordCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "recover_pass_generate",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface IRegistrationParamsCSRF {
  new_login: string;
  tcountry?: string;
  ref_url?: string;
}

type IRegistrationRequestCSRF = IRegistrationParamsCSRF &
  IStandardGUJRequestParams & {
    iaccept: number;
    autopwd: number;
    sw: number;
  };

interface IRegistrationResponseCSRF {
  status: string;
  redirect?: IResponseRedirect;
  actions_output: {
    error_code: number;
  };
}

export const registrationCSRF = createAsyncThunk(
  "user/registration/registrationCSRF",
  async (params: {
    data: IRegistrationParamsCSRF;
    onSuccess?: (success: TRegistrationCSRFNode) => void;
    onError?: (error: TRegistrationCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IRegistrationRequestCSRF,
      IRegistrationResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "registrationCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "register_user",
          iaccept: 1,
          autopwd: 1,
          sw: 1,
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export const registrationSlice = createSlice({
  name: "user/registration",
  initialState,
  reducers: {
    // Нет сеттеров
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "user.registration");
    // Загрузка
    builder.addCase(
      checkEmailAvailabilityGUJ.pending,
      (state: IUserRegistrationState) => {
        state.actions.checkEmailAvailabilityGUJ.isLoading = true;
        state.actions.checkEmailAvailabilityGUJ.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      checkEmailAvailabilityGUJ.fulfilled,
      (
        state: IUserRegistrationState,
        action: PayloadAction<TCheckEmailAvailabilityGUJNode>
      ) => {
        state.actions.checkEmailAvailabilityGUJ = action.payload;
        state.actions.checkEmailAvailabilityGUJ.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(
      verifyPhoneCSRF.pending,
      (state: IUserRegistrationState) => {
        state.actions.verifyPhoneCSRF.isLoading = true;
        state.actions.verifyPhoneCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      verifyPhoneCSRF.fulfilled,
      (
        state: IUserRegistrationState,
        action: PayloadAction<TVerifyPhoneCSRFNode>
      ) => {
        state.actions.verifyPhoneCSRF = action.payload;
        state.actions.verifyPhoneCSRF.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(
      recoverPasswordCSRF.pending,
      (state: IUserRegistrationState) => {
        state.actions.recoverPasswordCSRF.isLoading = true;
        state.actions.recoverPasswordCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      recoverPasswordCSRF.fulfilled,
      (
        state: IUserRegistrationState,
        action: PayloadAction<TRecoverPasswordCSRFNode>
      ) => {
        state.actions.recoverPasswordCSRF = action.payload;
        state.actions.recoverPasswordCSRF.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(
      registrationCSRF.pending,
      (state: IUserRegistrationState) => {
        state.actions.registrationCSRF.isLoading = true;
        state.actions.registrationCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      registrationCSRF.fulfilled,
      (
        state: IUserRegistrationState,
        action: PayloadAction<TRegistrationCSRFNode>
      ) => {
        state.actions.registrationCSRF = action.payload;
        state.actions.registrationCSRF.isLoading = false;
      }
    );
  },
});

export default registrationSlice.reducer;
